import type { CoachingSession } from '@efdigital/ll-addon-data';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface CancelSessionModalSignature {
  Args: {
    session: CoachingSession;
    incursPenalty: boolean;
  };
  Blocks: {
    default: [showModal: () => void];
  };
}

export class CancelSessionModal extends Component<CancelSessionModalSignature> {
  @service declare coach: CoachService;
  @tracked open = false;
  @tracked loading = false;
  @tracked cancelReason = '';

  closeCancelModal = () => {
    this.open = false;
  };

  get confirmButtonDisabled(): boolean {
    return this.cancelReason.trim().length === 0 || this.loading;
  }

  confirmCancel = async () => {
    const reason = this.cancelReason.trim();
    if (!reason) {
      return;
    }

    this.loading = true;
    try {
      await this.coach.cancelSession(this.args.session, reason);
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
      this.open = false;
    }
  };

  setCancelReason = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.cancelReason = target.value;
  };

  showModal = () => {
    this.cancelReason = '';
    this.open = true;
  };

  <template>
    {{#if this.open}}
      <BsModal @open={{this.open}} as |modal|>
        <modal.header>
          <h5 class="modal-title">Cancel Session</h5>
        </modal.header>
        <modal.body>
          <p>Are you sure you want to cancel this session?</p>
          {{#if @incursPenalty}}
            <p class="text-danger">You are cancelling this session at short
              notice.</p>
          {{/if}}

          <div class="mb-3">
            <label for="cancelReason" class="form-label">Reason for cancellation</label>
            <input
              type="text"
              class="form-control"
              id="cancelReason"
              required
              value={{this.cancelReason}}
              {{on "input" this.setCancelReason}}
            />
          </div>
        </modal.body>
        <modal.footer>
          <button
            class="btn btn-outline-secondary"
            type="button"
            {{on "click" this.closeCancelModal}}
            disabled={{this.loading}}
          >Do not cancel</button>
          <button
            class="btn btn-primary"
            type="button"
            {{on "click" this.confirmCancel}}
            disabled={{this.confirmButtonDisabled}}
          >Confirm</button>
        </modal.footer>
      </BsModal>
    {{/if}}
    {{yield this.showModal}}
  </template>
}
